// @ts-nocheck
import React, { useState } from 'react';
import { Lock, Mail, ArrowLeft } from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import logger from '../../utils/logger';
import './Login.css';

const Login = ({ onLogin, passwordRecoveryMode, onPasswordResetComplete, isDarkMode }) => {
  const [mode, setMode] = useState('login'); // 'login', 'signup', 'confirm', 'forgot', 'reset'
  const [credentials, setCredentials] = useState({ email: '', password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isInviteFlow, setIsInviteFlow] = useState(false);

  // Handle password recovery/invite mode from App.js (when user clicks email link)
  React.useEffect(() => {
    if (passwordRecoveryMode) {
      setMode('reset');
      setError('');
      setMessage('');
      // Check if this is an invite flow (new user) vs password reset (existing user)
      const hash = window.location.hash;
      const isInvite = hash.includes('type=invite') || hash.includes('type=signup');
      setIsInviteFlow(isInvite);
    }
  }, [passwordRecoveryMode]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (error) {
        if (error.message.includes('Email not confirmed')) {
          setError('Please check your email and confirm your account before signing in.');
        } else {
          setError(error.message);
        }
        return;
      }

      if (data.user) {
        onLogin(data.user, data.session);
      }
    } catch (err) {
      logger.error('Sign in error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    if (credentials.password.length < 6) {
      setError('Password must be at least 6 characters long.');
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email: credentials.email,
        password: credentials.password,
      });

      if (error) {
        setError(error.message);
        return;
      }

      if (data.user) {
        setMode('confirm');
        setMessage(`We've sent a confirmation email to ${credentials.email}. Please check your inbox and click the link to verify your account.`);
      }
    } catch (err) {
      logger.error('Sign up error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(credentials.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        setError(error.message);
        return;
      }

      setMessage(`Password reset email sent to ${credentials.email}. Please check your inbox.`);
    } catch (err) {
      logger.error('Password reset error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    // Validate passwords match
    if (credentials.password !== credentials.confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    // Validate password length
    if (credentials.password.length < 6) {
      setError('Password must be at least 6 characters long.');
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: credentials.password
      });

      if (error) {
        setError(error.message);
        return;
      }

      setMessage(isInviteFlow 
        ? 'Password set successfully! You can now sign in.' 
        : 'Password updated successfully! You can now sign in with your new password.');
      
      // Sign out to clear the recovery session
      await supabase.auth.signOut();
      
      // Notify parent component and switch to login mode
      if (onPasswordResetComplete) {
        onPasswordResetComplete();
      }
      
      // After a brief delay, switch to login mode
      setTimeout(() => {
        setMode('login');
        setCredentials({ email: '', password: '', confirmPassword: '' });
      }, 2000);
      
    } catch (err) {
      logger.error('Password reset error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    if (mode === 'login') {
      handleSignIn(e);
    } else if (mode === 'signup') {
      handleSignUp(e);
    } else if (mode === 'forgot') {
      handleForgotPassword(e);
    } else if (mode === 'reset') {
      handleResetPassword(e);
    }
  };

  const resetForm = () => {
    setCredentials({ email: '', password: '', confirmPassword: '' });
    setError('');
    setMessage('');
  };

  const switchMode = (newMode) => {
    resetForm();
    setMode(newMode);
  };

  // Confirmation screen after signup
  if (mode === 'confirm') {
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="login-logo">
            <img className="theme-logo" src={isDarkMode ? "/logo-with-name-dark.png" : "/logo-with-name.png"} alt="Logo" />
          </div>
          <div className="login-header">
            <h2>Check Your Email</h2>
            <p>Almost there!</p>
          </div>
          
          <div className="confirm-message">
            <div className="confirm-icon">✉️</div>
            <p>{message}</p>
            <p className="confirm-note">
              Didn't receive the email? Check your spam folder or try signing up again.
            </p>
          </div>
          
          <button
            type="button"
            className="login-button btn-animated"
            onClick={() => switchMode('login')}
          >
            <ArrowLeft size={18} style={{ marginRight: '8px' }} />
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-logo">
          <img className="theme-logo" src={isDarkMode ? "/logo-with-name-dark.png" : "/logo-with-name.png"} alt="Logo" />
        </div>
        <div className="login-header">
          <h2>{mode === 'reset' && isInviteFlow ? 'Welcome! 🎉' : 'Account Mapper'}</h2>
          <p>
            {mode === 'login' && 'Sign in to continue'}
            {mode === 'signup' && 'Create your account'}
            {mode === 'forgot' && 'Reset your password'}
            {mode === 'reset' && (isInviteFlow ? 'Set up your password to get started' : 'Set your new password')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {mode !== 'reset' && (
            <div className="input-group">
              <Mail className="input-icon" size={20} />
              <input
                type="email"
                placeholder="Email address"
                value={credentials.email}
                onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                required
                disabled={loading}
                autoComplete="email"
              />
            </div>
          )}

          {mode !== 'forgot' && (
            <div className="input-group">
              <Lock className="input-icon" size={20} />
              <input
                type="password"
                placeholder={mode === 'reset' ? 'New password' : 'Password'}
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                required
                disabled={loading}
                autoComplete={mode === 'signup' || mode === 'reset' ? 'new-password' : 'current-password'}
                minLength={mode === 'signup' || mode === 'reset' ? 6 : undefined}
              />
            </div>
          )}

          {mode === 'reset' && (
            <div className="input-group">
              <Lock className="input-icon" size={20} />
              <input
                type="password"
                placeholder="Confirm new password"
                value={credentials.confirmPassword}
                onChange={(e) => setCredentials({ ...credentials, confirmPassword: e.target.value })}
                required
                disabled={loading}
                autoComplete="new-password"
                minLength={6}
              />
            </div>
          )}

          {error && <div className="error-message">{error}</div>}
          {message && <div className="success-message">{message}</div>}

          <button type="submit" className="login-button btn-animated" disabled={loading}>
            {loading ? (
              mode === 'login' ? 'Signing In...' :
              mode === 'signup' ? 'Creating Account...' :
              mode === 'reset' ? (isInviteFlow ? 'Setting Password...' : 'Updating Password...') :
              'Sending...'
            ) : (
              mode === 'login' ? 'Sign In' :
              mode === 'signup' ? 'Create Account' :
              mode === 'reset' ? (isInviteFlow ? 'Set Password' : 'Update Password') :
              'Send Reset Link'
            )}
          </button>
        </form>

        <div className="auth-links">
          {mode === 'login' && (
            <button
              type="button"
              className="link-button"
              onClick={() => switchMode('forgot')}
            >
              Forgot password?
            </button>
          )}

          {mode === 'signup' && (
            <>
              <div className="auth-divider">
                <span>Already have an account?</span>
              </div>
              <button
                type="button"
                className="secondary-button"
                onClick={() => switchMode('login')}
              >
                Sign In
              </button>
            </>
          )}

          {mode === 'forgot' && (
            <button
              type="button"
              className="link-button"
              onClick={() => switchMode('login')}
            >
              <ArrowLeft size={16} style={{ marginRight: '4px' }} />
              Back to Sign In
            </button>
          )}

          {mode === 'reset' && (
            <p className="reset-note">
              {isInviteFlow 
                ? 'Choose a secure password (at least 6 characters). After setting it, you\'ll be able to sign in.'
                : 'Enter your new password above. After updating, you\'ll be redirected to sign in.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
